import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";
import { CART } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export default function CartPage() {
  const { items, setQty, removeItem, clear, totals } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div data-testid={CART.page} className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-hyd-panel grid place-items-center border border-hyd-border mb-4">
          <ShoppingBag className="w-8 h-8 text-hyd-text3" />
        </div>
        <h2 className="font-display text-2xl font-bold">Your cart is empty</h2>
        <p className="text-hyd-text2 mt-1">Start adding some Diwali sparkle!</p>
        <Button onClick={() => navigate("/")} className="mt-5 bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim">Browse crackers</Button>
      </div>
    );
  }

  return (
    <div data-testid={CART.page} className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl font-extrabold">Your cart</h1>
        <button data-testid={CART.clearBtn} onClick={clear} className="text-xs uppercase text-hyd-text2 hover:text-hyd-red flex items-center gap-1">
          <Trash2 className="w-3.5 h-3.5" /> Clear
        </button>
      </div>

      <div className="mt-5 grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2 space-y-3">
          {items.map((it) => (
            <div
              key={it.key}
              data-testid={CART.itemRow(it.key)}
              className="flex gap-3 bg-hyd-panel border border-hyd-border rounded-xl p-3"
            >
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-hyd-panel2 shrink-0">
                {it.image_url ? (
                  <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                ) : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm line-clamp-2">{it.name}</div>
                <div className="text-xs text-hyd-text2 mt-1">{it.kind === "combo" ? "Combo pack" : "Individual"}{it.is_green_cracker ? " • Green" : ""}</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-hyd-gold font-bold">{inr(it.unit_price * it.qty)}</div>
                  <div className="flex items-center gap-2 bg-hyd-bg border border-hyd-border rounded-full px-1 py-1">
                    <button aria-label="dec" onClick={() => setQty(it.kind, it.ref_id, it.qty - 1)} className="w-6 h-6 grid place-items-center text-hyd-red">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="min-w-[20px] text-center text-sm font-bold">{it.qty}</span>
                    <button aria-label="inc" onClick={() => setQty(it.kind, it.ref_id, it.qty + 1)} className="w-6 h-6 grid place-items-center text-hyd-gold">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
              <button aria-label="remove" onClick={() => removeItem(it.kind, it.ref_id)} className="text-hyd-text3 hover:text-hyd-red">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="bg-hyd-panel border border-hyd-border rounded-xl p-4 h-fit sticky top-20">
          <h3 className="font-display text-lg font-bold mb-3">Order summary</h3>
          <Row label="Subtotal" value={inr(totals.subtotal)} />
          <Row label="You save" value={`- ${inr(totals.savings)}`} valueClass="text-emerald-400" />
          <Row label="Delivery" value={totals.deliveryFee === 0 ? "FREE" : inr(totals.deliveryFee)} valueClass={totals.deliveryFee === 0 ? "text-emerald-400" : ""} />
          {totals.deliveryFee > 0 && (
            <div className="text-[11px] text-hyd-text2 mt-1">Add {inr(totals.freeDeliveryRemaining)} for free delivery</div>
          )}
          <div className="border-t border-hyd-border my-3" />
          <Row label="Total" value={inr(totals.total)} bold />
          <div className="p-2 rounded-md bg-hyd-bg border border-hyd-border text-[11px] text-hyd-text2 mt-3">
            ⚠ Fireworks are age-restricted (18+). You&apos;ll confirm at checkout.
          </div>
          <Button
            data-testid={CART.checkoutBtn}
            onClick={() => navigate("/checkout")}
            className="w-full mt-4 bg-hyd-red hover:bg-hyd-redDim text-white font-bold uppercase tracking-wider"
          >
            Proceed to checkout <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, valueClass = "", bold = false }) {
  return (
    <div className={`flex justify-between text-sm py-1 ${bold ? "font-bold text-base" : ""}`}>
      <span className="text-hyd-text2">{label}</span>
      <span className={valueClass || "text-hyd-text"}>{value}</span>
    </div>
  );
}
